import { Link } from "@/lib/router-compat";
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Clock, Calendar, Building2, Tag, Factory } from "lucide-react";
import { authorSlug, authorDisplayName, authorRole, parseAuthors } from "@/lib/authorSlug";
import { supabase } from "@/integrations/supabase/client";
import { getContentTypeMeta, type ContentTypeKey } from "@/lib/resourceTypes";
import type { CaseStudyMeta } from "@/lib/parseCaseStudyContent";
import { FILTER_DISPLAY_NAMES } from "@/components/blog/BlogFilterSidebar";
import { getDateLocale, uiT } from "@/i18n/ui";

interface ContentPostHeroProps {
  title: string;
  subtitle?: string | null;
  category?: string | null;
  industryCategory?: string | null;
  serviceCategory?: string | null;
  publishedAt?: string | null;
  author?: string | null;
  featuredImage?: string | null;
  readingTime: number;
  summary?: string | null;
  tags?: string[] | null;
  contentType: ContentTypeKey;
  caseStudyMeta?: CaseStudyMeta;
  rightVisual?: React.ReactNode;
}

const ContentPostHero = ({
  title,
  subtitle,
  category,
  industryCategory,
  serviceCategory,
  publishedAt,
  author,
  featuredImage,
  readingTime,
  summary,
  tags,
  contentType,
  caseStudyMeta,
  rightVisual,
}: ContentPostHeroProps) => {
  const lang = useLanguage();
  const t = uiT(lang);
  const dateLoc = getDateLocale(lang);
  const meta = getContentTypeMeta(contentType);
  const pillLabels = Array.from(
    new Set([industryCategory, serviceCategory, ...(tags ?? [])].filter(Boolean) as string[])
  ).slice(0, 4);

  const authors = author ? parseAuthors(author) : [];
  const authorSlugs = authors.map(a => authorSlug(a));

  const { data: authorProfiles } = useQuery({
    queryKey: ["author-avatars", authorSlugs],
    queryFn: async () => {
      const { data } = await supabase
        .from("authors")
        .select("avatar_url, name, slug")
        .in("slug", authorSlugs);
      return data ?? [];
    },
    enabled: authorSlugs.length > 0,
  });

  const profileMap = new Map(
    (authorProfiles ?? []).map(p => [p.slug, p])
  );

  const hasMetaInfo = caseStudyMeta && (caseStudyMeta.clientType || caseStudyMeta.services || caseStudyMeta.industry);

  return (
    <section className="relative min-h-[440px] lg:min-h-[500px] flex items-end overflow-hidden">
      {featuredImage ?
      <img src={featuredImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" /> :
      <div className="absolute inset-0 bg-primary" />
      }
      <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/85 to-primary/25" />

      <div className={`relative z-10 container mx-auto px-6 lg:px-10 max-w-6xl pb-12 pt-32 ${rightVisual ? 'flex flex-col lg:flex-row items-end gap-8' : ''}`}>
        <div className={rightVisual ? 'flex-1 min-w-0' : ''}>
          {/* Breadcrumb at the very top */}
          <nav aria-label="Breadcrumb" className="mb-8">
            <ol className="flex items-center gap-2 text-sm text-primary-foreground/50">
              <li><Link to={localizeHref("/", lang)} className="hover:text-primary-foreground transition-colors">{t("home")}</Link></li>
              <li aria-hidden="true">&gt;</li>
              <li><Link to={localizeHref(meta.route, lang)} className="hover:text-primary-foreground transition-colors">{meta.label}</Link></li>
              <li aria-hidden="true">&gt;</li>
              <li className="text-primary-foreground/70 truncate max-w-[200px]">{title}</li>
            </ol>
          </nav>

          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {/* Content type badge */}
          <span
            className="inline-block text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-5"
            style={{ backgroundColor: meta.color, color: "#fff" }}>
            {meta.label}
          </span>

          <h1 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-primary-foreground tracking-tight leading-[1.12]">
            {title}
          </h1>

          {subtitle && (
            <p className="mt-3 text-xl sm:text-2xl lg:text-3xl font-semibold text-primary-foreground/75 tracking-tight max-w-3xl">
              {subtitle}
            </p>
          )}

          {summary && (
            <div className="mt-5 text-base sm:text-lg text-primary-foreground/65 leading-relaxed max-w-3xl space-y-4">
              {summary.split(/\n\n+/).map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          )}

          {/* Tag pills — below title & description */}
          {pillLabels.length > 0 &&
          <div className="flex flex-wrap items-center gap-2 mt-6">
              {pillLabels.map((label) =>
            <Link
              key={label}
              to={localizeHref(`/en/knowledge-center?filter=${encodeURIComponent(label)}`, lang)}
              className="text-[11px] font-bold bg-primary-foreground/15 text-primary-foreground/90 px-3.5 py-1.5 rounded-full uppercase tracking-wider backdrop-blur-sm border border-primary-foreground/10 hover:bg-primary-foreground/25 transition-colors">
                  {FILTER_DISPLAY_NAMES[label] ?? label}
                </Link>
            )}
            </div>
          }

          <div className="mt-7 flex flex-wrap items-center gap-4 text-sm text-primary-foreground/55">
            {/* Multiple authors */}
            {authors.length > 0 && (
              <div className="flex items-center gap-3">
                {/* Stacked avatars */}
                <div className="flex items-center -space-x-2">
                  {authors.map((a) => {
                    const slug = authorSlug(a);
                    const profile = profileMap.get(slug);
                    return (
                      <Link
                        key={slug}
                        to={localizeHref(`/en/author/${slug}`, lang)}
                        className="relative hover:z-10 transition-transform hover:scale-110"
                      >
                        {profile?.avatar_url ? (
                          <img
                            src={profile.avatar_url}
                            alt={authorDisplayName(a)}
                            className="w-11 h-11 rounded-full object-cover ring-2 ring-primary/80 shrink-0"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-full bg-primary-foreground/15 flex items-center justify-center text-primary-foreground/70 text-sm font-bold shrink-0 ring-2 ring-primary/80">
                            {authorDisplayName(a).charAt(0)}
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
                {/* Names */}
                <div className="text-left">
                  {authors.length === 1 ? (
                    <Link
                      to={localizeHref(`/en/author/${authorSlug(authors[0])}`, lang)}
                      className="hover:opacity-80 transition-opacity"
                    >
                      <span className="font-semibold text-primary-foreground/85 block text-[0.9375rem]">
                        {authorDisplayName(authors[0])}
                      </span>
                      {authorRole(authors[0]) && (
                        <span className="text-xs text-primary-foreground/45 block mt-0.5 max-w-[280px] truncate">
                          {authorRole(authors[0])}
                        </span>
                      )}
                    </Link>
                  ) : (
                    <div className="flex flex-wrap items-center gap-x-1">
                      {authors.map((a, i) => (
                        <span key={authorSlug(a)}>
                          <Link
                            to={localizeHref(`/en/author/${authorSlug(a)}`, lang)}
                            className="font-semibold text-primary-foreground/85 text-[0.9375rem] hover:opacity-80 transition-opacity"
                          >
                            {authorDisplayName(a)}
                          </Link>
                          {i < authors.length - 1 && (
                            <span className="text-primary-foreground/45">{i === authors.length - 2 ? " & " : ", "}</span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {authors.length > 0 && publishedAt && <span className="w-1 h-1 rounded-full bg-primary-foreground/25" aria-hidden="true" />}
            {publishedAt &&
            <span className="inline-flex items-center gap-1.5">
                <Calendar size={14} />
                {new Date(publishedAt).toLocaleDateString(dateLoc, { month: "long", day: "numeric", year: "numeric" })}
              </span>
            }
            {(publishedAt || authors.length > 0) && readingTime > 0 && <span className="w-1 h-1 rounded-full bg-primary-foreground/25" aria-hidden="true" />}
            {readingTime > 0 && (
            <span className="inline-flex items-center gap-1.5">
              <Clock size={14} />
              {readingTime} {t("minRead")}
            </span>
            )}
          </div>

          {/* Case study meta pills */}
          {hasMetaInfo}
        </motion.div>
        </div>
        {rightVisual && (
          <div className="hidden lg:flex flex-col items-center gap-6 flex-shrink-0">
            {rightVisual}
          </div>
        )}
      </div>
    </section>);

};

export default ContentPostHero;